import { action, makeObservable, observable } from "mobx";
import { GetProductsParams } from "../../../models/Products/GetProductsParams";
import { SortType } from "../../../models/SortType";
import { FilterValue } from "../../../models/Filters/FilterValue";
import { FilterProductsType } from "../../../models/Products/Product";
import {
  PodCatalogListQuery,
  PodCatalogSortBy,
  PodCatalogSortType,
} from "../../../models/Products/PodCatalog";

export const DEFAULT_PRICE_RANGE: [number, number] = [10, 50_000];

const clampPriceRange = (min: number, max: number): [number, number] => {
  let minPrice = min;
  let maxPrice = max;
  if (!Number.isFinite(minPrice)) minPrice = DEFAULT_PRICE_RANGE[0];
  if (!Number.isFinite(maxPrice)) maxPrice = DEFAULT_PRICE_RANGE[1];
  if (minPrice < DEFAULT_PRICE_RANGE[0]) minPrice = DEFAULT_PRICE_RANGE[0];
  if (maxPrice > DEFAULT_PRICE_RANGE[1]) maxPrice = DEFAULT_PRICE_RANGE[1];
  if (maxPrice < minPrice) maxPrice = minPrice;
  return [minPrice, maxPrice];
};

const defaultProductParams = (): GetProductsParams => ({
  page_no: 1,
  limit: 30,
  sortBy: "created_date",
  sortType: SortType.DESC,
  category_id: [],
  vendor_id: [],
  active: true,
});

const defaultPodParams = (): PodCatalogListQuery => ({
  page_no: 1,
  limit: 30,
  sort_by: "created_date",
  sort_type: "DESC",
});

const parseSortType = (raw: string | null | undefined): SortType | undefined => {
  if (raw === SortType.ASC || raw === SortType.DESC) return raw as SortType;
  return undefined;
};

export class ProductListingStore {
  listingTab: number;
  isLoading: boolean;
  podLoading: boolean;
  isFiltersExpanded: boolean;
  isPodFiltersExpanded: boolean;

  params: GetProductsParams;
  podParams: PodCatalogListQuery;

  searchTerm: string;
  podSearchInput: string;

  categoryFilters: FilterValue[];
  selectedCategoriesIds: number[];
  podSelectedCategoriesIds: number[];

  pendingActive: boolean;
  filterProductsType: FilterProductsType;

  priceRange: [number, number];
  minPriceInput: string;
  maxPriceInput: string;

  podPriceRange: [number, number];
  podMinPriceInput: string;
  podMaxPriceInput: string;

  addToPodLoadingId: number | null;
  removingFromPodProductId: number | null;

  constructor() {
    this.listingTab = 0;
    this.isLoading = false;
    this.podLoading = false;
    this.isFiltersExpanded = false;
    this.isPodFiltersExpanded = false;

    this.params = defaultProductParams();
    this.podParams = defaultPodParams();

    this.searchTerm = "";
    this.podSearchInput = "";

    this.categoryFilters = [];
    this.selectedCategoriesIds = [];
    this.podSelectedCategoriesIds = [];

    this.pendingActive = true;
    this.filterProductsType = FilterProductsType.AllProducts;

    this.priceRange = [...DEFAULT_PRICE_RANGE];
    this.minPriceInput = String(DEFAULT_PRICE_RANGE[0]);
    this.maxPriceInput = String(DEFAULT_PRICE_RANGE[1]);

    this.podPriceRange = [...DEFAULT_PRICE_RANGE];
    this.podMinPriceInput = String(DEFAULT_PRICE_RANGE[0]);
    this.podMaxPriceInput = String(DEFAULT_PRICE_RANGE[1]);

    this.addToPodLoadingId = null;
    this.removingFromPodProductId = null;

    makeObservable(this, {
      listingTab: observable,
      isLoading: observable,
      podLoading: observable,
      isFiltersExpanded: observable,
      isPodFiltersExpanded: observable,
      params: observable,
      podParams: observable,
      searchTerm: observable,
      podSearchInput: observable,
      categoryFilters: observable,
      selectedCategoriesIds: observable,
      podSelectedCategoriesIds: observable,
      pendingActive: observable,
      filterProductsType: observable,
      priceRange: observable,
      minPriceInput: observable,
      maxPriceInput: observable,
      podPriceRange: observable,
      podMinPriceInput: observable,
      podMaxPriceInput: observable,
      addToPodLoadingId: observable,
      removingFromPodProductId: observable,
      reset: action,
      hydrateFromSearchParams: action,
      setListingTab: action,
      setIsLoading: action,
      setPodLoading: action,
      toggleFiltersExpanded: action,
      togglePodFiltersExpanded: action,
      setCategoryFilters: action,
      setSelectedCategoriesIds: action,
      setPodSelectedCategoriesIds: action,
      setPendingActive: action,
      setFilterProductsType: action,
      syncFilterStateFromParams: action,
      setPriceRange: action,
      setPodPriceRange: action,
      handleMinPriceChange: action,
      handleMaxPriceChange: action,
      setPodMinPriceInput: action,
      setPodMaxPriceInput: action,
      setPodSearchInput: action,
      syncPriceInputsFromRange: action,
      syncPodPriceInputsFromRange: action,
      syncSelectedCategoriesFromParams: action,
      applyDebouncedProductSearch: action,
      applyDebouncedPodSearch: action,
      handleSearchBar: action,
      handleSearchSubmit: action,
      handleKeyDownSearch: action,
      handleApplyFilters: action,
      resetFilters: action,
      handlePodApply: action,
      resetPodFilters: action,
      handlePageChange: action,
      handleRowsPerPageChange: action,
      productsSortChange: action,
      podHandlePageChange: action,
      podHandleRowsPerPageChange: action,
      podSortChange: action,
      patchPodParams: action,
      setAddToPodLoadingId: action,
      setRemovingFromPodProductId: action,
    });
  }

  reset = () => {
    this.listingTab = 0;
    this.isLoading = false;
    this.podLoading = false;
    this.isFiltersExpanded = false;
    this.isPodFiltersExpanded = false;
    this.params = defaultProductParams();
    this.podParams = defaultPodParams();
    this.searchTerm = "";
    this.podSearchInput = "";
    this.categoryFilters = [];
    this.selectedCategoriesIds = [];
    this.podSelectedCategoriesIds = [];
    this.pendingActive = true;
    this.filterProductsType = FilterProductsType.AllProducts;
    this.priceRange = [...DEFAULT_PRICE_RANGE];
    this.minPriceInput = String(DEFAULT_PRICE_RANGE[0]);
    this.maxPriceInput = String(DEFAULT_PRICE_RANGE[1]);
    this.podPriceRange = [...DEFAULT_PRICE_RANGE];
    this.podMinPriceInput = String(DEFAULT_PRICE_RANGE[0]);
    this.podMaxPriceInput = String(DEFAULT_PRICE_RANGE[1]);
    this.addToPodLoadingId = null;
    this.removingFromPodProductId = null;
  };

  hydrateFromSearchParams = (searchParams: URLSearchParams) => {
    const isPodTab =
      searchParams.get("pod") === "1" || searchParams.get("tab") === "pod";

    if (isPodTab) {
      this.listingTab = 1;
      const next: PodCatalogListQuery = { ...this.podParams };
      const pageNo = Number(searchParams.get("page_no"));
      if (Number.isFinite(pageNo) && pageNo >= 1) next.page_no = pageNo;
      const limit = Number(searchParams.get("limit"));
      if (Number.isFinite(limit) && limit > 0) next.limit = limit;
      const sortBy = searchParams.get("sort_by");
      if (sortBy) next.sort_by = sortBy as PodCatalogSortBy;
      const sortType = searchParams.get("sort_type");
      if (sortType === "ASC" || sortType === "DESC") next.sort_type = sortType;
      const search = searchParams.get("search");
      if (search != null) {
        this.podSearchInput = search;
        next.search = search || undefined;
      }
      this.podParams = next;
      return;
    }

    const next: GetProductsParams = { ...this.params };

    const pageNo = searchParams.get("page");
    if (pageNo != null && pageNo !== "") {
      const n = Number(pageNo);
      if (Number.isFinite(n)) next.page_no = n;
    }

    const limit = searchParams.get("limit");
    if (limit != null && limit !== "") {
      const n = Number(limit);
      if (Number.isFinite(n)) next.limit = n;
    }

    const sortBy = searchParams.get("sort_by");
    if (sortBy) next.sortBy = sortBy;

    const sortType = parseSortType(searchParams.get("sort_type"));
    if (sortType) next.sortType = sortType;

    const name = searchParams.get("name");
    if (name != null) {
      this.searchTerm = name;
      next.name = name || undefined;
    }

    const activeRaw = searchParams.get("active");
    if (activeRaw === "true" || activeRaw === "false") {
      const av = activeRaw === "true";
      this.pendingActive = av;
      next.active = av;
    }

    const fastProcurementRaw = searchParams.get("fast_procurement");
    if (fastProcurementRaw === "true" || fastProcurementRaw === "1") {
      this.filterProductsType = FilterProductsType.FastProcurementProduct;
      next.fast_procurement = true;
    } else {
      this.filterProductsType = FilterProductsType.AllProducts;
      next.fast_procurement = undefined;
    }

    const cat = searchParams.get("category_id");
    if (cat) {
      const ids = cat
        .split(",")
        .map((s) => Number(s.trim()))
        .filter((n) => Number.isFinite(n));
      if (ids.length) {
        next.category_id = ids;
        this.selectedCategoriesIds = ids;
      }
    }

    const minP = searchParams.get("min_price");
    const maxP = searchParams.get("max_price");
    if (minP != null && minP !== "") {
      const n = Number(minP);
      if (Number.isFinite(n)) {
        next.min_price = n;
        this.priceRange = [n, this.priceRange[1]];
      }
    }
    if (maxP != null && maxP !== "") {
      const n = Number(maxP);
      if (Number.isFinite(n)) {
        next.max_price = n;
        this.priceRange = [this.priceRange[0], n];
      }
    }
    this.priceRange = clampPriceRange(this.priceRange[0], this.priceRange[1]);
    if (next.min_price != null || next.max_price != null) {
      next.min_price = this.priceRange[0];
      next.max_price = this.priceRange[1];
    }
    this.minPriceInput = String(this.priceRange[0]);
    this.maxPriceInput = String(this.priceRange[1]);

    this.params = next;
  };

  setListingTab = (tab: number) => {
    this.listingTab = tab;
  };

  setIsLoading = (v: boolean) => {
    this.isLoading = v;
  };

  setPodLoading = (v: boolean) => {
    this.podLoading = v;
  };

  toggleFiltersExpanded = () => {
    this.isFiltersExpanded = !this.isFiltersExpanded;
  };

  togglePodFiltersExpanded = () => {
    this.isPodFiltersExpanded = !this.isPodFiltersExpanded;
  };

  patchPodParams = (patch: Partial<PodCatalogListQuery>) => {
    this.podParams = { ...this.podParams, ...patch };
  };

  setCategoryFilters = (filters: FilterValue[]) => {
    this.categoryFilters = filters;
  };

  setSelectedCategoriesIds = (ids: number[]) => {
    this.selectedCategoriesIds = ids;
  };

  setPodSelectedCategoriesIds = (ids: number[]) => {
    this.podSelectedCategoriesIds = ids;
  };

  setPendingActive = (v: boolean) => {
    this.pendingActive = v;
  };

  setFilterProductsType = (value: FilterProductsType) => {
    this.filterProductsType = value;
  };

  syncFilterStateFromParams = () => {
    this.pendingActive = this.params.active ?? true;
    this.filterProductsType =
      this.params.fast_procurement === true
        ? FilterProductsType.FastProcurementProduct
        : FilterProductsType.AllProducts;
  };

  setPriceRange = (range: [number, number]) => {
    this.priceRange = range;
  };

  setPodPriceRange = (range: [number, number]) => {
    this.podPriceRange = range;
  };

  setPodMinPriceInput = (v: string) => {
    this.podMinPriceInput = v;
  };

  setPodMaxPriceInput = (v: string) => {
    this.podMaxPriceInput = v;
  };

  setPodSearchInput = (v: string) => {
    this.podSearchInput = v;
  };

  handleMinPriceChange = (v: string) => {
    this.minPriceInput = v;
  };

  handleMaxPriceChange = (v: string) => {
    this.maxPriceInput = v;
  };

  syncPriceInputsFromRange = () => {
    this.minPriceInput = String(this.priceRange[0]);
    this.maxPriceInput = String(this.priceRange[1]);
  };

  syncPodPriceInputsFromRange = () => {
    this.podMinPriceInput = String(this.podPriceRange[0]);
    this.podMaxPriceInput = String(this.podPriceRange[1]);
  };

  syncSelectedCategoriesFromParams = () => {
    const ids = this.params.category_id;
    this.selectedCategoriesIds = ids && ids.length ? [...ids] : [];
  };

  applyDebouncedProductSearch = (trimmed: string) => {
    const previousName = this.params.name || "";
    if (previousName === trimmed) return;
    this.params = {
      ...this.params,
      name: trimmed || undefined,
      page_no: 1,
    };
  };

  applyDebouncedPodSearch = (trimmed: string) => {
    const previousSearch = this.podParams.search || "";
    if (previousSearch === trimmed) return;
    this.podParams = {
      ...this.podParams,
      page_no: 1,
      search: trimmed ? trimmed : undefined,
    };
  };

  handleSearchBar = (value: string) => {
    if (value.length !== 0) {
      this.searchTerm = value;
    } else {
      this.searchTerm = "";
      this.params = {
        ...this.params,
        name: "",
      };
    }
  };

  handleSearchSubmit = () => {
    this.params = {
      ...this.params,
      name: this.searchTerm,
      page_no: 1,
    };
  };

  handleKeyDownSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (this.searchTerm.trim().length > 0) {
        this.params = {
          ...this.params,
          name: this.searchTerm,
          page_no: 1,
        };
      } else {
        this.searchTerm = "";
        this.params = {
          ...this.params,
          name: "",
          page_no: 1,
        };
      }
    }
  };

  handleApplyFilters = () => {
    const minRaw = this.minPriceInput.trim()
      ? Number(this.minPriceInput)
      : this.priceRange[0];
    const maxRaw = this.maxPriceInput.trim()
      ? Number(this.maxPriceInput)
      : this.priceRange[1];
    const [minPrice, maxPrice] = clampPriceRange(minRaw, maxRaw);

    this.priceRange = [minPrice, maxPrice];
    this.minPriceInput = String(minPrice);
    this.maxPriceInput = String(maxPrice);

    this.params = {
      ...this.params,
      page_no: 1,
      category_id: this.selectedCategoriesIds,
      active: this.pendingActive,
      fast_procurement:
        this.filterProductsType === FilterProductsType.FastProcurementProduct
          ? true
          : undefined,
      min_price: minPrice,
      max_price: maxPrice,
    };
  };

  resetFilters = () => {
    this.searchTerm = "";
    this.selectedCategoriesIds = [];
    this.pendingActive = true;
    this.filterProductsType = FilterProductsType.AllProducts;
    this.priceRange = [...DEFAULT_PRICE_RANGE];
    this.minPriceInput = String(DEFAULT_PRICE_RANGE[0]);
    this.maxPriceInput = String(DEFAULT_PRICE_RANGE[1]);
    this.params = {
      page_no: 1,
      name: "",
      limit: this.params.limit ?? 30,
      sortBy: this.params.sortBy ?? "created_date",
      sortType: this.params.sortType ?? SortType.DESC,
      category_id: [],
      vendor_id: [],
      active: true,
      fast_procurement: undefined,
      min_price: undefined,
      max_price: undefined,
    };
  };

  handlePodApply = () => {
    const minRaw = this.podMinPriceInput.trim()
      ? Number(this.podMinPriceInput)
      : this.podPriceRange[0];
    const maxRaw = this.podMaxPriceInput.trim()
      ? Number(this.podMaxPriceInput)
      : this.podPriceRange[1];
    const [minPrice, maxPrice] = clampPriceRange(minRaw, maxRaw);

    this.podPriceRange = [minPrice, maxPrice];
    this.podMinPriceInput = String(minPrice);
    this.podMaxPriceInput = String(maxPrice);

    this.podParams = {
      ...this.podParams,
      page_no: 1,
      category_ids:
        this.podSelectedCategoriesIds.length > 0
          ? this.podSelectedCategoriesIds
          : undefined,
      min_price: minPrice,
      max_price: maxPrice,
    };
  };

  resetPodFilters = () => {
    this.podSearchInput = "";
    this.podSelectedCategoriesIds = [];
    this.podPriceRange = [...DEFAULT_PRICE_RANGE];
    this.podMinPriceInput = String(DEFAULT_PRICE_RANGE[0]);
    this.podMaxPriceInput = String(DEFAULT_PRICE_RANGE[1]);
    this.podParams = {
      ...defaultPodParams(),
      sort_by: this.podParams.sort_by,
      sort_type: this.podParams.sort_type,
      limit: this.podParams.limit,
    };
  };

  handlePageChange = (page: number) => {
    this.params = { ...this.params, page_no: page };
  };

  handleRowsPerPageChange = (limit: number) => {
    this.params = { ...this.params, limit, page_no: 1 };
  };

  productsSortChange = (column: string, sortType: SortType) => {
    this.params = {
      ...this.params,
      sortBy: column,
      sortType,
    };
  };

  podHandlePageChange = (page: number) => {
    this.podParams = { ...this.podParams, page_no: page };
  };

  podHandleRowsPerPageChange = (limit: number) => {
    this.podParams = { ...this.podParams, limit, page_no: 1 };
  };

  podSortChange = (column: PodCatalogSortBy, sortType: PodCatalogSortType) => {
    this.podParams = {
      ...this.podParams,
      sort_by: column,
      sort_type: sortType,
      page_no: 1,
    };
  };

  setAddToPodLoadingId = (id: number | null) => {
    this.addToPodLoadingId = id;
  };

  setRemovingFromPodProductId = (id: number | null) => {
    this.removingFromPodProductId = id;
  };
}
